import React, { lazy } from 'react';
import { Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import {
  DYNAMIC_ROUTES_CONSTANT,
  RELATIVE_ROUTES,
  ROUTES,
} from '../constants/RouteConstants';
import ErrorBoundaryView from '../components/Common/ErrorBoundary/ErrorBoundary';
import APIError from '../components/Common/APIError/APIError';
import Loader from '../components/Common/Loader/Loader';
import { captureException } from '../utils/sentry';
import { HTTP_STATUS_CODES } from '../constants/HttpConstants';
import { IAPIErrorProps } from 'types/components/Common/apiError.types';

const MainLayout = lazy(() => import('../Layouts/MainLayout/MainLayout'));
const PublicLayout = lazy(() => import('../Layouts/PublicLayout/PublicLayout'));
const CampaignLayout = lazy(
  () => import('../Layouts/CampaignLayout/CampaignLayout')
);

const RedirectView = lazy(() => import('../views/Redirect/Redirect'));

const InterviewDetailLayout = lazy(
  () => import('../Layouts/InterviewDetailLayout/InterviewDetailLayout')
);

const BackofficeCompanyDetailLayout = lazy(
  () =>
    import(
      '../Layouts/BackofficeCompanyDetailLayout/BackofficeCompanyDetailLayout'
    )
);

const ScheduleInterview = lazy(
  () => import('../views/Interviews/ScheduleInterview/ScheduleInterview')
);

const EditScheduleInterview = lazy(
  () =>
    import(
      /* webpackChunkName: "EditScheduledInterviewChunk" */ '../views/Interviews/EditScheduledInterview/EditScheduledInterview'
    )
);

const EvaluationCriteria = lazy(
  () => import('../views/Interviews/EvaluationCriteria/EvaluationCriteria')
);

const ProblemLibrary = lazy(
  () => import('../views/Interviews/ProblemLibrary/ProblemLibrary')
);
const JobRoles = lazy(() => import('../views/Interviews/JobRoles/JobRoles'));
const InterviewManager = lazy(
  () => import('../views/Interviews/InterviewManager/InterviewManager')
);

const JobRoleSettings = lazy(
  () => import('../views/Interviews/JobRoleSettings/JobRoleSettings')
);

const CandidateReport = lazy(
  () => import('../views/Interviews/CandidateReport/CandidateReport')
);

const Interview = lazy(() => import('../views/Interviews'));

const AiInterview = lazy(
  () => import('../views/Interviews/AiInterview/AiInterview')
);
const TeamRoles = lazy(
  () => import('../views/recruiterDashboard/TeamRoles/TeamRoles')
);

const RecruiterBilling = lazy(
  () => import('../views/recruiterDashboard/Billing/Billing')
);

const BackofficeRoles = lazy(() => import('../views/backOffice/Roles/Roles'));

const BackofficeUsage = lazy(() => import('../views/backOffice/Usage/Usage'));

const DhwaniDashboard = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/DhwaniDashboard/DhwaniDashboard')
);

const CampaignDetails = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/CampaignDetails/CampaignDetails')
);

const QuestionsList = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/QuestionsList/QuestionsList')
);

const CreateQuestion = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/CreateQuestion/CreateQuestion')
);

const CandidateDetails = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/CandidateDetails/CandidateDetails')
);

const LaunchCampaign = lazy(
  /* webpackChunkName: "DhwaniApplicationChunk" */ () =>
    import('../views/Dhwani/LaunchCampaign/LaunchCampaign')
);

const {
  STAR_ROUTE,
  INTERVIEW_ROUTE,
  SCHEDULE_INTERVIEW_ROUTE,
  EDIT_SCHEDULE_INTERVIEW_ROUTE,
  INTERVIEW_EVALUATION_CRITERIA_ROUTE,
  INTERVIEW_SETTINGS_ROUTE,
  INTERVIEW_MANAGER_ROUTE,
  CANDIDATE_REPORT_ROUTE,
  PROBLEM_LIBRARY,
  DHWANI_CAMPAIGN_DETAILS_ROUTE,
  DHWANI_CAMPAIGN_ROUTE,
  DHWANI_CAMPAIGN_QUESTIONS_ROUTE,
  DHWANI_CREATE_CAMPAIGN_QUESTION_ROUTE,
  DHWANI_CAMPAIGN_QUESTION_ROUTE,
  DHWANI_CAMPAIGN_CANDIDATE_DETAILS_ROUTE,
  DHWANI_CAMPAIGN_LAUNCH_ROUTE,
} = ROUTES;

const {
  INTERVIEWS,
  ROOT,
  PUBLIC,
  RECRUIT,
  SSO_ROUTE,
  AI_INTERVIEW,
  MANUAL_INTERVIEW,
  TEAM_SETTINGS,
  BACKOFFICE,
  DHWANI,
} = RELATIVE_ROUTES;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logError = (error: Error, info: React.ErrorInfo) => {
  captureException(error);
};

const APP_ROUTER = [
  {
    element: (
      <ErrorBoundary FallbackComponent={ErrorBoundaryView} onError={logError}>
        <Outlet />
      </ErrorBoundary>
    ),
    children: [
      {
        path: SSO_ROUTE,
        element: <Outlet />,
        children: [
          {
            path: `auth/${DYNAMIC_ROUTES_CONSTANT.SSO_ACCESS_CODE}`,
            element: <RedirectView />,
          },
          {
            path: 'interview/:interviewSlug/report/:reportSlug',
            element: <CandidateReport isPublic={false} isSso={true} />,
          },
          {
            path: STAR_ROUTE,
            element: <APIError status="404" />,
          },
        ],
      },
      {
        path: PUBLIC,
        element: <PublicLayout />,
        children: [
          {
            path: 'interview/:interviewSlug/report/:reportSlug',
            element: <CandidateReport isPublic={true} isSso={false} />,
          },
        ],
      },
      {
        path: ROOT,
        element: <MainLayout />,
        children: [
          {
            path: RECRUIT,
            children: [
              {
                path: INTERVIEWS,
                children: [
                  {
                    path: '',
                    element: <Interview />,
                  },
                  {
                    path: MANUAL_INTERVIEW,
                    element: <JobRoles />,
                  },
                  {
                    path: AI_INTERVIEW,
                    element: <AiInterview />,
                  },
                ],
              },
              {
                path: DHWANI,
                element: <DhwaniDashboard />,
              },
            ],
          },
          {
            path: '/cocaller/campaign/create',
            element: <CampaignLayout />,
            children: [
              {
                index: true,
                element: <CampaignDetails isEdit={false} />,
              },
            ],
          },
          {
            path: DHWANI_CAMPAIGN_ROUTE,
            element: <CampaignLayout />,
            children: [
              {
                path: DHWANI_CAMPAIGN_DETAILS_ROUTE,
                element: <CampaignDetails isEdit={true} />,
              },
              {
                path: DHWANI_CAMPAIGN_QUESTIONS_ROUTE,
                element: <QuestionsList />,
              },
              {
                path: DHWANI_CREATE_CAMPAIGN_QUESTION_ROUTE,
                element: <CreateQuestion />,
              },
              {
                path: DHWANI_CAMPAIGN_QUESTION_ROUTE,
                element: <CreateQuestion isEdit={true} />,
              },
              {
                path: DHWANI_CAMPAIGN_CANDIDATE_DETAILS_ROUTE,
                element: <CandidateDetails />,
              },
              {
                path: DHWANI_CAMPAIGN_LAUNCH_ROUTE,
                element: <LaunchCampaign />,
              },
            ],
          },
          {
            path: 'billing',
            element: <RecruiterBilling />,
          },
          {
            path: TEAM_SETTINGS,
            children: [
              {
                path: 'roles',
                element: <TeamRoles />,
              },
            ],
          },
          {
            path: BACKOFFICE,
            element: <BackofficeCompanyDetailLayout />,
            children: [
              {
                path: `company/${DYNAMIC_ROUTES_CONSTANT.COMPANY_SLUG}/roles`,
                element: <BackofficeRoles />,
              },
              {
                path: `company/${DYNAMIC_ROUTES_CONSTANT.COMPANY_SLUG}/usage`,
                element: <BackofficeUsage />,
              },
            ],
          },
          {
            path: INTERVIEW_ROUTE,
            element: <InterviewDetailLayout />,
            children: [
              {
                path: STAR_ROUTE,
                element: (
                  <APIError
                    status={
                      HTTP_STATUS_CODES.NOT_FOUND.toString() as IAPIErrorProps['status']
                    }
                  />
                ),
              },
              {
                path: INTERVIEW_EVALUATION_CRITERIA_ROUTE,
                element: <EvaluationCriteria />,
              },
              {
                path: INTERVIEW_SETTINGS_ROUTE,
                element: <JobRoleSettings />,
              },
              {
                path: SCHEDULE_INTERVIEW_ROUTE,
                element: <ScheduleInterview />,
              },
              {
                path: EDIT_SCHEDULE_INTERVIEW_ROUTE,
                element: <EditScheduleInterview />,
              },
              {
                path: INTERVIEW_MANAGER_ROUTE,
                element: <InterviewManager />,
              },
              {
                path: CANDIDATE_REPORT_ROUTE,
                element: <CandidateReport isPublic={false} isSso={false} />,
              },
              {
                path: PROBLEM_LIBRARY,
                element: <ProblemLibrary />,
              },
            ],
          },
        ],
      },
      {
        path: STAR_ROUTE,
        element: (
          <APIError
            status={
              HTTP_STATUS_CODES.NOT_FOUND.toString() as IAPIErrorProps['status']
            }
          />
        ),
      },
    ],
  },
];

const Routes = () => {
  const router = createBrowserRouter(APP_ROUTER);

  return <RouterProvider router={router} fallbackElement={<Loader />} />;
};

export default Routes;
